/**
 * Comprehensive error handling and fallback mechanisms for the video call application
 */
export class ErrorHandler {
    constructor() {
        this.errorCount = 0;
        this.maxErrors = 3;
        this.errorHistory = [];
        this.fallbackStrategies = new Map();
        this.setupGlobalErrorHandling();
    }

    /**
     * Setup global error handling
     */
    setupGlobalErrorHandling() {
        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            // Keep console.error for critical system errors
            console.error('Unhandled promise rejection:', event.reason);
            this.handleError(event.reason, 'unhandled-rejection');
        });

        // Handle global errors
        window.addEventListener('error', (event) => {
            // Keep console.error for critical system errors
            console.error('Global error:', event.error);
            this.handleError(event.error, 'global-error');
        });

        // Handle WebRTC errors
        if (window.RTCPeerConnection) {
            const originalRTCPeerConnection = window.RTCPeerConnection;
            window.RTCPeerConnection = function(...args) {
                const pc = new originalRTCPeerConnection(...args);

                // Wrap error-prone methods
                const originalCreateOffer = pc.createOffer.bind(pc);
                pc.createOffer = async function(...args) {
                    try {
                        return await originalCreateOffer(...args);
                    } catch (error) {
                        console.error('RTCPeerConnection.createOffer error:', error);
                        throw error;
                    }
                };

                const originalCreateAnswer = pc.createAnswer.bind(pc);
                pc.createAnswer = async function(...args) {
                    try {
                        return await originalCreateAnswer(...args);
                    } catch (error) {
                        console.error('RTCPeerConnection.createAnswer error:', error);
                        throw error;
                    }
                };

                return pc;
            };
        }
    }

    /**
     * Handle errors with fallback strategies
     */
    handleError(error, context = 'unknown') {
        this.errorCount++;
        this.errorHistory.push({
            error,
            context,
            timestamp: Date.now(),
            stack: error.stack
        });

        console.error(`Error in ${context}:`, error);

        // Check if we've exceeded max errors
        if (this.errorCount > this.maxErrors) {
            this.triggerEmergencyFallback();
            return;
        }

        // Try to recover using fallback strategies
        this.attemptRecovery(error, context);
    }

    /**
     * Attempt to recover from an error
     */
    attemptRecovery(error, context) {
        const strategy = this.fallbackStrategies.get(context);
        if (strategy && typeof strategy === 'function') {
            try {
                strategy(error);
            } catch (fallbackError) {
                console.error('Fallback strategy failed:', fallbackError);
                this.handleError(fallbackError, 'fallback-failure');
            }
        }
    }

    /**
     * Add a fallback strategy for a specific error context
     */
    addFallbackStrategy(context, strategy) {
        this.fallbackStrategies.set(context, strategy);
    }

    /**
     * Trigger emergency fallback when too many errors occur
     */
    triggerEmergencyFallback() {
        console.warn('Too many errors, triggering emergency fallback');

        // Stop all media streams
        this.stopAllMediaStreams();

        // Reset WebRTC connections
        this.resetWebRTCConnections();

        // Show emergency error screen
        this.showEmergencyErrorScreen();

        // Reset error count after emergency fallback
        setTimeout(() => {
            this.errorCount = 0;
        }, 10000);
    }

    /**
     * Stop all active media streams
     */
    stopAllMediaStreams() {
        try {
            // Get all video and audio elements
            const mediaElements = document.querySelectorAll('video, audio');
            mediaElements.forEach(element => {
                if (element.srcObject) {
                    const stream = element.srcObject;
                    if (stream instanceof MediaStream) {
                        stream.getTracks().forEach(track => {
                            track.stop();
                        });
                    }
                    element.srcObject = null;
                }
            });
        } catch (error) {
            console.error('Failed to stop media streams:', error);
        }
    }

    /**
     * Reset WebRTC connections
     */
    resetWebRTCConnections() {
        try {
            // Close all RTCPeerConnections
            if (window.RTCPeerConnection) {
                // This is a bit hacky, but we can't easily track all instances
                // The application should handle this in its own cleanup methods
                console.log('WebRTC connections should be closed by the application');
            }
        } catch (error) {
            console.error('Failed to reset WebRTC connections:', error);
        }
    }

    /**
     * Show emergency error screen
     */
    showEmergencyErrorScreen() {
        try {
            // Create emergency error screen
            const emergencyScreen = document.createElement('div');
            emergencyScreen.id = 'emergency-error-screen';
            emergencyScreen.className = 'emergency-error-screen';
            emergencyScreen.innerHTML = `
                <div class="emergency-content">
                    <h2>⚠️ Emergency Mode</h2>
                    <p>Too many errors occurred. The application has been reset for safety.</p>
                    <button id="emergency-retry-btn" class="emergency-btn">Try Again</button>
                    <button id="emergency-refresh-btn" class="emergency-btn">Refresh Page</button>
                </div>
            `;

            // Add styles
            const style = document.createElement('style');
            style.textContent = `
                .emergency-error-screen {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    color: white;
                    font-family: Arial, sans-serif;
                }
                .emergency-content {
                    text-align: center;
                    padding: 2rem;
                    background: rgba(0, 0, 0, 0.3);
                    border-radius: 20px;
                    backdrop-filter: blur(10px);
                }
                .emergency-btn {
                    background: rgba(255, 255, 255, 0.2);
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    color: white;
                    padding: 1rem 2rem;
                    margin: 0.5rem;
                    border-radius: 10px;
                    cursor: pointer;
                    font-size: 1rem;
                    transition: all 0.2s ease;
                }
                .emergency-btn:hover {
                    background: rgba(255, 255, 255, 0.3);
                    transform: scale(1.05);
                }
            `;

            document.head.appendChild(style);
            document.body.appendChild(emergencyScreen);

            // Add event listeners
            document.getElementById('emergency-retry-btn').addEventListener('click', () => {
                this.retryApplication();
            });

            document.getElementById('emergency-refresh-btn').addEventListener('click', () => {
                window.location.reload();
            });

        } catch (error) {
            console.error('Failed to show emergency error screen:', error);
            // Last resort - reload the page
            window.location.reload();
        }
    }

    /**
     * Retry the application after emergency fallback
     */
    retryApplication() {
        try {
            // Remove emergency screen
            const emergencyScreen = document.getElementById('emergency-error-screen');
            if (emergencyScreen) {
                emergencyScreen.remove();
            }

            // Reset error count
            this.errorCount = 0;

            // Dispatch retry event
            window.dispatchEvent(new CustomEvent('application-retry'));

        } catch (error) {
            console.error('Failed to retry application:', error);
            window.location.reload();
        }
    }

    /**
     * Get error statistics
     */
    getErrorStats() {
        return {
            totalErrors: this.errorCount,
            maxErrors: this.maxErrors,
            errorHistory: this.errorHistory,
            isInEmergencyMode: this.errorCount > this.maxErrors
        };
    }

    /**
     * Reset error handler state
     */
    reset() {
        this.errorCount = 0;
        this.errorHistory = [];
        this.fallbackStrategies.clear();
    }

    /**
     * Create a retry mechanism for async operations
     */
    createRetryMechanism(operation, maxRetries = 3, delay = 1000) {
        return async (...args) => {
            let lastError;

            for (let attempt = 1; attempt <= maxRetries; attempt++) {
                try {
                    return await operation(...args);
                } catch (error) {
                    lastError = error;
                    console.warn(`Operation failed (attempt ${attempt}/${maxRetries}):`, error);

                    if (attempt < maxRetries) {
                        await new Promise(resolve => setTimeout(resolve, delay * attempt));
                    }
                }
            }

            throw lastError;
        };
    }

    /**
     * Create a circuit breaker for operations
     */
    createCircuitBreaker(operation, failureThreshold = 5, recoveryTimeout = 60000) {
        let failures = 0;
        let lastFailureTime = 0;
        let state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN

        return async (...args) => {
            const now = Date.now();

            // Check if circuit breaker is open
            if (state === 'OPEN') {
                if (now - lastFailureTime > recoveryTimeout) {
                    state = 'HALF_OPEN';
                } else {
                    throw new Error('Circuit breaker is OPEN - operation blocked');
                }
            }

            try {
                const result = await operation(...args);

                // Success - close circuit breaker
                if (state === 'HALF_OPEN') {
                    state = 'CLOSED';
                    failures = 0;
                }

                return result;
            } catch (error) {
                failures++;
                lastFailureTime = now;

                if (failures >= failureThreshold) {
                    state = 'OPEN';
                }

                throw error;
            }
        };
    }
}

// Create global error handler instance
export const globalErrorHandler = new ErrorHandler();
